// The promise API over the Typst worker. One worker holds one wasm instance; calls queue in order. A trap (a
// Rust panic aborts on wasm32) poisons the instance, so the worker is discarded, the call fails with a
// `TypstError` whose `trap` is set, and the next call starts a fresh worker with the same fonts.
import { fontsFor, type FontSelection } from './fonts.ts';
import type { Call, CompiledLayout, CompiledPDF, CompileOptions, FontAliases, Reply } from './protocol.ts';

/** The worker as the client sees it: a browser `Worker` or Node's `worker_threads.Worker`, adapted. */
export interface WorkerPort {
  post(message: unknown, transfer?: ArrayBuffer[]): void;
  /** Called with each reply, and with an Error when the worker dies. */
  listen(onReply: (reply: Reply) => void, onDeath: (error: Error) => void): void;
  terminate(): void;
}

export interface TypstOptions {
  /** Starts a worker. */
  spawn: () => WorkerPort;
  /** A font file's bytes, by name in fonts/. */
  loadFont: (file: string) => Promise<Uint8Array>;
  /** Where the worker finds the wasm-bindgen glue and the wasm (defaults: pkg/ beside the worker). */
  glue?: string;
  wasm?: string | Uint8Array;
}

export class TypstError extends Error {
  /** The engine trapped (panicked) and was restarted. */
  readonly trap: boolean;
  constructor(message: string, trap: boolean) {
    super(message);
    this.name = 'TypstError';
    this.trap = trap;
  }
}

interface Pending {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
}

export class Typst {
  private port?: WorkerPort;
  private nextID = 1;
  private pending = new Map<number, Pending>();
  /** The last fonts set, replayed into a restarted worker. */
  private fonts?: { bytes: Uint8Array[]; aliases?: FontAliases; key?: string };
  private fontCache = new Map<string, Promise<Uint8Array>>();
  /** Workers started (1 + restarts), and the largest wasm memory seen, in bytes. */
  workersStarted = 0;
  peakMemory = 0;

  constructor(private readonly options: TypstOptions) {}

  /** Loads the faces a Typst `font:` list names (`fontsFor`) and hands them to the engine; skipped when unchanged. */
  async setFonts(families: readonly string[]): Promise<FontSelection> {
    const selection = fontsFor(families);
    if (this.fonts?.key === selection.key) return selection;
    const bytes = await Promise.all(selection.files.map((f) => this.font(f)));
    await this.setFontBytes(bytes, selection.aliases, selection.key);
    return selection;
  }

  /** Hands font files (OTF/TTF/TTC) to the engine; returns the number of faces. */
  async setFontBytes(bytes: Uint8Array[], aliases?: FontAliases, key?: string): Promise<number> {
    this.fonts = { bytes, aliases, key };
    return (await this.call({ op: 'setFonts', fonts: bytes, aliases })) as number;
  }

  /** Typst markup → PDF. Throws `TypstError` with Typst's diagnostics ("line N: …"). */
  async compilePDF(source: string, options?: CompileOptions): Promise<CompiledPDF> {
    return this.track((await this.call({ op: 'compilePDF', source, options })) as CompiledPDF);
  }

  /** Typst markup → PDF and the layout: every line with its source byte offsets (TypstKit's layout JSON). */
  async compileLayout(source: string, options?: CompileOptions): Promise<CompiledLayout> {
    return this.track((await this.call({ op: 'compileLayout', source, options })) as CompiledLayout);
  }

  /** Drops Typst's memoised results older than `maxAge` compiles; 0 empties the cache (do so between print passes). */
  async evict(maxAge = 0): Promise<void> {
    if (!this.port) return;
    await this.call({ op: 'evict', maxAge });
  }

  /** Family names the engine has (aliases included). */
  async families(): Promise<string[]> {
    return (await this.call({ op: 'families' })) as string[];
  }

  /** The wasm instance's linear memory, in bytes. */
  async memory(): Promise<number> {
    return (await this.call({ op: 'memory' })) as number;
  }

  /** Makes the engine panic, to test recovery. */
  async panicForTest(): Promise<void> {
    await this.call({ op: 'panic' });
  }

  /** Stops the worker (handing its memory back); the next call starts another. */
  terminate(): void {
    this.discard(new TypstError('The Typst engine was stopped', false));
  }

  private track<T extends CompiledPDF>(result: T): T {
    this.peakMemory = Math.max(this.peakMemory, result.memory);
    return result;
  }

  private font(file: string): Promise<Uint8Array> {
    let bytes = this.fontCache.get(file);
    if (!bytes) {
      bytes = this.options.loadFont(file);
      this.fontCache.set(file, bytes);
      bytes.catch(() => this.fontCache.delete(file));
    }
    return bytes;
  }

  /** `replayFonts`: hand the new worker the fonts the last one had (after a restart). */
  private start(replayFonts: boolean): WorkerPort {
    const port = this.options.spawn();
    this.workersStarted++;
    port.listen(
      (reply) => {
        const pending = this.pending.get(reply.id);
        if (!pending) return;
        this.pending.delete(reply.id);
        if (reply.ok) return pending.resolve(reply.result);
        if (reply.trap) this.discard();
        pending.reject(new TypstError(reply.error, reply.trap));
      },
      (error) => this.discard(new TypstError(`The Typst engine stopped: ${error.message}`, true), port),
    );
    this.port = port;
    void this.send(port, { op: 'init', glue: this.options.glue, wasm: this.options.wasm }).catch(() => {});
    if (replayFonts && this.fonts) {
      // Copied, not transferred: the client keeps the bytes for the next restart. A failure here fails the call after.
      void this.send(port, { op: 'setFonts', fonts: this.fonts.bytes, aliases: this.fonts.aliases }).catch(() => {});
    }
    return port;
  }

  /** Throws the worker away; pending calls fail with `error` (none: they've been answered). */
  private discard(error?: TypstError, port = this.port): void {
    if (!port || port !== this.port) return;
    this.port = undefined;
    port.terminate();
    for (const [id, pending] of this.pending) {
      this.pending.delete(id);
      pending.reject(error ?? new TypstError('The Typst engine stopped', true));
    }
  }

  private send(port: WorkerPort, call: Call): Promise<unknown> {
    const id = this.nextID++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      port.post({ ...call, id });
    });
  }

  private call(call: Call): Promise<unknown> {
    return this.send(this.port ?? this.start(call.op !== 'setFonts'), call);
  }
}

/** The browser globals `browserSpawn` and `fetchFonts` use (declared here so the package also typechecks without DOM). */
interface BrowserGlobals {
  location?: { href: string; origin: string };
  trustedTypes?: { createPolicy(name: string, rules: { createScriptURL(url: string): string }): { createScriptURL(url: string): unknown } };
  Worker: new (url: unknown, options: { type: 'module'; name?: string }) => {
    postMessage(message: unknown, transfer: ArrayBuffer[]): void;
    onmessage: ((event: { data: Reply }) => void) | null;
    onerror: ((event: { message?: string }) => void) | null;
    terminate(): void;
  };
  caches?: { open(name: string): Promise<{ match(url: string): Promise<FetchResponse | undefined>; put(url: string, response: FetchResponse): Promise<void> }> };
  fetch(url: string): Promise<FetchResponse>;
}
interface FetchResponse { ok: boolean; status: number; clone(): FetchResponse; arrayBuffer(): Promise<ArrayBuffer> }
const browser = () => globalThis as unknown as BrowserGlobals;

/** A Trusted Types policy that makes script URLs (what `trustedTypes.createPolicy` returns). */
export interface ScriptURLPolicy { createScriptURL(url: string): unknown }

/** A module Web Worker (browser). `url` is the worker script (a bundled worker.ts). Under a CSP with
 * `require-trusted-types-for 'script'`, the URL goes through a Trusted Types policy named `policy`, which the CSP's
 * `trusted-types` directive must list; it accepts same-origin URLs only. An app that starts other workers under the
 * same policy name makes the policy itself and passes it (a name can be made into a policy only once). */
export function browserSpawn(url: string | URL, policy: string | ScriptURLPolicy = 'writing-suite-typst'): () => WorkerPort {
  let trusted: ScriptURLPolicy | undefined = typeof policy === 'string' ? undefined : policy;
  return () => {
    const g = browser();
    const href = new URL(url, g.location?.href).href;
    if (g.trustedTypes && !trusted && typeof policy === 'string') {
      trusted = g.trustedTypes.createPolicy(policy, {
        createScriptURL: (u) => {
          if (new URL(u, g.location?.href).origin !== g.location?.origin) throw new TypeError('The Typst worker must be same-origin');
          return u;
        },
      });
    }
    const worker = new g.Worker(trusted ? trusted.createScriptURL(href) : href, { type: 'module', name: 'typst' });
    return {
      post: (message, transfer) => worker.postMessage(message, transfer ?? []),
      listen: (onReply, onDeath) => {
        worker.onmessage = (event) => onReply(event.data);
        worker.onerror = (event) => onDeath(new Error(event.message || 'worker error'));
      },
      terminate: () => worker.terminate(),
    };
  };
}

/** Fetches a font from `base` (a URL ending in /), keeping it in Cache Storage when there is one. */
export function fetchFonts(base: string | URL, cacheName = 'writing-suite-typst-fonts'): (file: string) => Promise<Uint8Array> {
  return async (file) => {
    const g = browser();
    const url = new URL(file, base).href;
    const cache = g.caches ? await g.caches.open(cacheName).catch(() => undefined) : undefined;
    let response = await cache?.match(url);
    if (!response) {
      response = await g.fetch(url);
      if (!response.ok) throw new Error(`Couldn't load the font ${file} (${response.status})`);
      await cache?.put(url, response.clone()).catch(() => {});
    }
    return new Uint8Array(await response.arrayBuffer());
  };
}
